<template>
    <div class="divider" @mousedown="handleMouseDown" @touchstart.prevent="handleTouchStart">
        <div class="arrow-icon icon arrows-vertical" />
        <div class="line"></div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';

    @Component({
        emits: ['moveDivider'],
    })
    export default class BoardDivider extends Vue {
        private previousY: number | null = null;

        public mounted(): void {
            window.addEventListener('mouseup', this.handleMouseUp);
            window.addEventListener('touchend', this.handleTouchEnd);
            window.addEventListener('blur', this.handleBlur);
        }

        public beforeUnmount(): void {
            window.removeEventListener('mouseup', this.handleMouseUp);
            window.removeEventListener('mousemove', this.handleMouseMove);
            window.removeEventListener('touchmove', this.handleTouchMove);
            window.removeEventListener('blur', this.handleBlur);
        }

        private updateHeight(y: number): void {
            if (this.previousY) {
                const offset: number = y - this.previousY;
                if (!isNaN(offset)) {
                    this.$emit('moveDivider', offset);
                }
                this.previousY = y;
            }
        }

        private finishHeight(y?: number): void {
            if (y !== undefined) {
                this.updateHeight(y);
            }

            document.body.style.cursor = 'default';
            this.previousY = null;
        }

        public handleMouseDown(e: MouseEvent): void {
            this.previousY = e.pageY;
            window.addEventListener('mousemove', this.handleMouseMove);
            document.body.style.cursor = 'row-resize';
        }

        private handleMouseMove(e: MouseEvent): void {
            this.updateHeight(e.pageY);
        }

        private handleMouseUp(e: MouseEvent): void {
            this.finishHeight(e.pageY);
            window.removeEventListener('mousemove', this.handleMouseMove);
        }

        public handleTouchStart(e: TouchEvent): void {
            if (e.touches.length > 1) {
                // ignore all additional touches
                return;
            }

            this.previousY = e.touches[0].pageY;
            window.addEventListener('touchmove', this.handleTouchMove);
            document.body.style.cursor = 'row-resize';
        }

        private handleTouchMove(e: TouchEvent): void {
            // assume first touch is relevant
            this.updateHeight(e.touches[0].pageY);
        }

        private handleTouchEnd(e: TouchEvent): void {
            if (e.touches.length > 0) {
                // there are still touches, don't finish
                return;
            }

            this.finishHeight(e.changedTouches[0].pageY);
            window.removeEventListener('touchmove', this.handleTouchMove);
        }

        private handleBlur(): void {
            this.finishHeight();
            window.removeEventListener('mousemove', this.handleMouseMove);
            window.removeEventListener('touchmove', this.handleTouchMove);
        }
    }
</script>

<style lang="scss" scoped>
    .divider {
        --divider-background-color: #9c9c9c;
        margin: 0 15px;
        position: relative;
        cursor: row-resize;

        .arrow-icon {
            display: flex;
            position: absolute;
            width: 100%;
            top: 1px;
            justify-content: center;
            transform: translateY(-0.03rem);
            height: 1.25rem;
            background: var(--divider-background-color);
        }

        .line {
            height: 11px;
            background: var(--divider-background-color);
            margin: 5px 15px 4px;
            border: 4px solid var(--main-background-color);
        }
    }
</style>
